import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { Role } from '../../../types/user';
import UserForm from './UserForm';
import { useNavigate, useOutletContext } from 'react-router';
import { disableNavigationGuard } from '../../../hooks/useNavigationGuard';
import { UserTemplateContext } from './types';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const CreateUserPage = () => {
  const { createUser } = useIotsApi();
  const { setDisplayedUserName } = useOutletContext<UserTemplateContext>();
  const { t } = useLanguage();
  const mutation = useMutation({ mutationFn: createUser });
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const navigate = useNavigate();

  return (
    <UserForm
      isEditingByDefault
      onCancel={() => void navigate('/admin/users')}
      onFullNameChange={setDisplayedUserName}
      onSubmit={values => {
        mutation.mutate(
          {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            operatorId: values.operatorId || undefined,
            role: values.role as Role,
          },
          {
            onSuccess: userId => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.user-creation-success'),
              });
              void disableNavigationGuard(() =>
                navigate(`/admin/users/${userId}`),
              );
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.user-creation-error'),
                ),
              });
              mutation.reset();
            },
          },
        );
      }}
      isLoading={mutation.isPending}
    />
  );
};

export default CreateUserPage;
